/**
 * The UI's one line to the model layer (§19). `/ui` never holds a key and
 * never imports `/server`: it POSTs a context the sim assembled and gets a
 * contract back, already validated, to hand to the reducer.
 *
 * The reply is a stream of newline-delimited JSON, so prose reaches the
 * screen while it is still being written. Only the final contract is worth
 * anything to the world; the streamed text is for the eye.
 *
 * The game is fun with the AI off (§1, rule 1), so every failure here falls
 * back to the sim's own words. But falling back **silently** is how a total
 * production outage hid for several releases: the functions were not
 * deployed, `/api/narrate` answered with the app's own HTML, this layer read
 * that as "no model", and the game played on in card lines with nothing but
 * a two-word label to show for it. So the states are now distinguished and
 * each one says something different:
 *
 *   unreachable — the endpoint is not a function. Nothing is deployed.
 *   no_key      — the function is there; the server has no API key.
 *   failing     — the function and key are there; the call did not work.
 */

import type { RenderContext, SceneContract } from '@sim/index.ts';

export type ModelStatus = 'unknown' | 'on' | 'no_key' | 'unreachable' | 'failing';

/** Everything that is not a working model, for the UI to word. */
export type ModelFault = Exclude<ModelStatus, 'unknown' | 'on'>;

let status: ModelStatus = 'unknown';

export function modelStatus(): ModelStatus {
  return status;
}

/** One short sentence for a fault, for the sheet. */
export function faultWords(fault: ModelFault): string {
  switch (fault) {
    case 'unreachable':
      return 'The narrator is not reachable: the server has no /api/narrate. You are getting written lines only.';
    case 'no_key':
      return 'The narrator has no API key on the server. You are getting written lines only.';
    case 'failing':
      return 'The narrator did not answer. You are getting written lines only.';
  }
}

/** Two or three words for the status tab. */
export function faultLabel(fault: ModelFault): string {
  switch (fault) {
    case 'unreachable':
      return 'narrator missing';
    case 'no_key':
      return 'no API key';
    case 'failing':
      return 'narrator failing';
  }
}

/**
 * A response that is not JSON is the tell for "no function here": the SPA
 * catch-all answers every unmatched path with `index.html`, at status 200.
 */
async function readJson(res: Response): Promise<unknown | null> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('json')) return null;
  try {
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

/** Ask the server what it has. Cheap, once, at load. Spends nothing. */
export async function probeModel(): Promise<ModelStatus> {
  try {
    const res = await fetch('/api/narrate', { method: 'GET' });
    const body = await readJson(res);
    if (body === null) {
      // Reachable in the HTTP sense, but whatever answered is not the
      // function. Almost always: the functions did not deploy.
      status = 'unreachable';
      return status;
    }
    if (res.status === 503) {
      status = 'no_key';
      return status;
    }
    status = (body as { model?: unknown }).model === 'on' ? 'on' : 'no_key';
  } catch {
    status = 'unreachable';
  }
  return status;
}

export type ModelAnswer =
  | { readonly ok: true; readonly contract: SceneContract; readonly model: string }
  | { readonly ok: false; readonly fault: ModelFault; readonly reason: string };

export interface AskHandlers {
  /** A piece of prose, as it is written. Append it. */
  readonly onProse?: (delta: string) => void;
  /** That reply was rejected. Throw away everything streamed so far. */
  readonly onReset?: () => void;
}

interface Frame {
  readonly t?: unknown;
  readonly d?: unknown;
  readonly error?: unknown;
  readonly contract?: SceneContract;
  readonly model?: unknown;
}

function fail(fault: ModelFault, reason: string): ModelAnswer {
  status = fault;
  return { ok: false, fault, reason };
}

export async function askModel(context: RenderContext, handlers: AskHandlers = {}): Promise<ModelAnswer> {
  let res: Response;
  try {
    res = await fetch('/api/narrate', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ kind: context.kind, context }),
    });
  } catch {
    return fail('unreachable', 'no connection');
  }

  const type = res.headers.get('content-type') ?? '';
  if (res.status === 503) {
    const body = (await readJson(res)) as { message?: string } | null;
    return fail('no_key', body?.message ?? 'the server has no API key');
  }
  if (!type.includes('ndjson')) {
    // Not our stream. Either the function is absent (HTML from the SPA
    // catch-all) or the platform answered for it.
    const body = (await readJson(res)) as { error?: string } | null;
    if (body === null) return fail('unreachable', `no function at /api/narrate (answered ${res.status} ${type || 'with no type'})`);
    return fail('failing', body.error ?? `http ${res.status}`);
  }
  if (!res.ok || !res.body) return fail('failing', `http ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let answer: ModelAnswer | null = null;

  const take = (line: string) => {
    let frame: Frame;
    try {
      frame = JSON.parse(line) as Frame;
    } catch {
      return; // a torn line is not worth a failed scene
    }
    if (frame.t === 'prose' && typeof frame.d === 'string') handlers.onProse?.(frame.d);
    else if (frame.t === 'reset') handlers.onReset?.();
    else if (frame.t === 'done' && frame.contract) {
      status = 'on';
      answer = { ok: true, contract: frame.contract, model: typeof frame.model === 'string' ? frame.model : '' };
    } else if (frame.t === 'error') {
      const reason = typeof frame.error === 'string' ? frame.error : 'unknown';
      answer = fail(reason === 'model_off' ? 'no_key' : 'failing', reason);
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      for (;;) {
        const nl = buffer.indexOf('\n');
        if (nl === -1) break;
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (line !== '') take(line);
      }
    }
    const rest = buffer.trim();
    if (rest !== '') take(rest);
  } catch {
    return fail('failing', 'the connection dropped mid-answer');
  }

  return answer ?? fail('failing', 'the stream ended without an answer');
}
